import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';

import { catchError, from, Observable, switchMap, throwError } from 'rxjs';

import { AuthService } from 'app/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private readonly authService: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authToken: string | null = this.authService.getAuthToken();
    const cognitoAccessToken: string | null = this.authService.getCognitoAccessToken();

    // Clone request with appropriate token
    const authReq = this.addAuthHeader(request, authToken || cognitoAccessToken);

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403 || error.status === 401) {
          // Try to refresh the token
          return from(this.handleTokenRefresh()).pipe(
            switchMap(() => {
              // Get new token after refresh
              const newToken =
                this.authService.getAuthToken() || this.authService.getCognitoAccessToken();
              const newAuthReq = this.addAuthHeader(request, newToken);
              return next.handle(newAuthReq);
            }),
            catchError(refreshError => {
              this.authService.$isUser.next(false);
              return throwError(() => refreshError);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  private addAuthHeader(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    if (!token) {
      return request;
    }
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private async handleTokenRefresh(): Promise<void> {
    return this.authService.currentSession();
  }
}
